import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { PlannedResourceRow } from 'components/IAP/hooks/assignmentListHooks';
import AssignmentCardResourceAttachmentDialog from './AssignmentCardResourceAttachmentDialog';
import Ics204aButtonSignatureProgress from './Ics204aButtonSignatureProgress';
import PlannedResourcePopoutButton from './PlannedResourcePopoutButton';
import {
  Ics204CheckboxField,
  Ics204NumberField,
  Ics204TextField,
} from './Ics204Fields';
import type {
  AssignmentCardFormData,
  PlannedResourceDraft,
} from './assignmentCardFormTypes';
import {
  ICS204_DELETE_BTN,
  ICS204_ICON_BTN,
  ICS204_OUTLINE_BTN,
} from './assignmentCardStyles';

const AssignmentCardPlannedResourceEdit = ({
  resourceId: id,
  periodNumber,
  walaId,
  showRowActions = false,
  isSaving = false,
  onConfirm,
  onCancel,
}: {
  resourceId: string;
  periodNumber?: number;
  walaId?: string | null;
  /** Tip-path per-row Check/Cancel. Full-card section edit omits these. */
  showRowActions?: boolean;
  isSaving?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}) => {
  const { control, setValue } = useFormContext<AssignmentCardFormData>();
  const [showDialog, setShowDialog] = useState(false);

  const draft = useWatch({ control, name: `resource.${id}` });
  const has204a = !!draft?.has204a;
  const isDocumentationEntry = !!draft?.isDocumentationEntry;

  // The 204A dialog takes the same row shape the read-only table uses, so the
  // unsaved draft is adapted to it rather than read back from the server.
  const dialogData = {
    id,
    name: draft?.resourceName ?? '',
    type: '',
    ...draft,
    walaId: walaId ?? null,
    isDocumentationEntry,
    documentationName: isDocumentationEntry ? draft?.resourceName : null,
  } as PlannedResourceRow;

  const commit204a = (fields: Partial<PlannedResourceDraft>) => {
    for (const [key, value] of Object.entries(fields)) {
      setValue(
        `resource.${id}.${key as keyof PlannedResourceDraft}`,
        value as never,
        { shouldDirty: true }
      );
    }
  };

  return (
    <>
      <TableRow data-testid="ics204-edit-resource-row">
        <TableCell />
        <TableCell>
          <Ics204TextField
            fieldName={`resource.${id}.resourceName`}
            testId="ics204-edit-resource-name"
            disabled={!isDocumentationEntry || isSaving}
          />
        </TableCell>
        <TableCell>
          <Ics204TextField
            fieldName={`resource.${id}.leader`}
            testId="ics204-edit-resource-leader"
            disabled={isSaving}
          />
        </TableCell>
        <TableCell>
          <Ics204NumberField
            fieldName={`resource.${id}.numberOfPersons`}
            testId="ics204-edit-resource-persons"
            min={0}
            step={1}
            disabled={isSaving}
            rules={{
              min: { value: 0, message: 'Cannot be negative' },
              pattern: {
                value: /^\d+$/,
                message: 'Must be a whole number',
              },
            }}
          />
        </TableCell>
        <TableCell>
          <Ics204TextField
            fieldName={`resource.${id}.contactInfo`}
            testId="ics204-edit-resource-contact-info"
            disabled={isSaving}
          />
        </TableCell>
        <TableCell>
          <Ics204TextField
            fieldName={`resource.${id}.reportingInfo`}
            testId="ics204-edit-resource-reporting-info"
            disabled={isSaving}
          />
        </TableCell>
        <TableCell>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-end tw:gap-2">
            <Ics204CheckboxField
              label="204A"
              fieldName={`resource.${id}.has204a`}
              testId="ics204-edit-resource-204a"
              disabled={isSaving}
            />
            {has204a ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={ICS204_OUTLINE_BTN}
                title="View or edit 204A details"
                data-testid="ics204-edit-resource-204a-button"
                disabled={isSaving}
                onClick={() => setShowDialog(true)}
              >
                <Ics204aButtonSignatureProgress
                  walaId={walaId}
                  plannedResourceKey={id}
                />
                204A
              </Button>
            ) : null}
            {/* Fixed-width slot so 204A stays aligned when popout is absent. */}
            {!isDocumentationEntry ? (
              <PlannedResourcePopoutButton
                plannedResource={dialogData}
                onCommit={commit204a}
                periodNumber={periodNumber}
              />
            ) : (
              <div className="tw:h-8 tw:w-8 tw:shrink-0" aria-hidden />
            )}
            {showRowActions ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Confirm planned resource"
                  className={ICS204_ICON_BTN}
                  data-testid="ics204-edit-confirm-resource"
                  disabled={isSaving}
                  onClick={onConfirm}
                >
                  <Check className="tw:h-4 tw:w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Cancel planned resource edit"
                  className={ICS204_DELETE_BTN}
                  data-testid="ics204-edit-cancel-resource"
                  disabled={isSaving}
                  onClick={onCancel}
                >
                  <X className="tw:h-4 tw:w-4" />
                </Button>
              </>
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {showDialog && has204a ? (
        <AssignmentCardResourceAttachmentDialog
          onClose={() => setShowDialog(false)}
          data={dialogData}
          onCommit={commit204a}
          periodNumber={periodNumber}
        />
      ) : null}
    </>
  );
};

export default AssignmentCardPlannedResourceEdit;
